// Home widget registry — Ships 1-3 of the widget board (Aug 25-26, 2026).
//
// Mirrors the homeNav DESTS pattern: one entry per widget, role-gated, with
// per-role preloaded boards. There is deliberately NO customize UI — boards are
// role defaults only (telemetry showed zero rearranging demand; the monthly
// ui_events review re-ranks these defaults instead). Per-person overrides are
// HAND-SET here when someone's usage clearly diverges from their role.
//
// "today" is the mobile-only Today hero (Greg: "I don't want to have to swipe
// down to look at what I can do that day") — it renders above everything on
// phones and not at all on desktop, where the day-detail column already covers
// it. The rest render in the widget board between the schedule and My Business.

export type WidgetKey =
  | "today"
  | "pipeline"
  | "call_list"
  | "approvals"
  | "company_funnel"
  | "ar"
  | "queues"
  | "iink"
  | "flights"
  | "rs_meetings"
  | "preinsp"
  // Ship 3 — Chad/ops, Robert/builds, Greg/exec.
  | "build_queue"
  | "coaching"
  | "referrals"
  | "builds_week"
  | "roof_master"
  | "pnl"
  | "leaderboard"
  // Aug 26 — company build revenue for the field (Greg: reps should "know
  // where we are as a company", and supplements stay out because they don't
  // get paid on them).
  | "company_revenue"
  // Sep 25 — Ops Requests: the queue of things the sales team needs from the
  // Roof Team Manager. Top of Robert's board.
  | "ops_requests"
  // Oct 19 — the canvas areas this person is assigned to (renders nothing
  // when they have none).
  | "my_areas";

export interface WidgetDef {
  key: WidgetKey;
  icon: string;
  title: string;
  /** Roles that may see this widget (super-users do NOT bypass — boards stay role-shaped). */
  roles: string[];
  /** Where the widget's "Open →" leads. */
  deepLink: string;
  /**
   * Per-person carve-out by email, mirroring homeNav's `Dest.excludeEmails`.
   * A carve-out BEATS the role gate and beats super-user status — the Aug 21
   * financials rule (Greg: "lets take jeremy out") is enforced in the DB by
   * `financials_gate()`, and this is the client mirror so the widget is never
   * rendered for someone whose RPC would 42501.
   */
  excludeEmails?: string[];
}

const SALES_ROLES = ["restore_manager", "team_lead", "asm"];
const FINANCE_TIER = ["finance", "admin", "sales_director"];

export const WIDGETS: Record<WidgetKey, WidgetDef> = {
  today: {
    key: "today",
    icon: "📅",
    title: "Today",
    roles: SALES_ROLES,
    deepLink: "/my-pipeline",
  },
  pipeline: {
    key: "pipeline",
    icon: "📋",
    title: "My Pipeline",
    roles: SALES_ROLES,
    deepLink: "/my-pipeline",
  },
  call_list: {
    key: "call_list",
    icon: "☎️",
    title: "Storm Call List",
    // Matches the /call-list DEST roles (minus office — no home board there yet).
    roles: [
      "admin",
      "sales_director",
      "asm",
      "team_lead",
      "restore_manager",
      "director_of_operations",
      "roof_director",
    ],
    deepLink: "/call-list",
  },
  approvals: {
    key: "approvals",
    icon: "🔨",
    title: "Approvals",
    roles: ["asm", "admin", "sales_director"],
    deepLink: "/asm",
  },
  company_funnel: {
    key: "company_funnel",
    icon: "📋",
    title: "Company Pipeline",
    roles: ["asm", "admin", "sales_director", "director_of_operations", "roof_director"],
    deepLink: "/my-pipeline",
  },
  ar: {
    key: "ar",
    icon: "💰",
    title: "AR Aging",
    roles: FINANCE_TIER,
    deepLink: "/finance?tab=ar",
  },
  queues: {
    key: "queues",
    icon: "🧾",
    title: "Queues",
    roles: FINANCE_TIER,
    deepLink: "/finance",
  },
  iink: {
    key: "iink",
    icon: "📮",
    title: "Checks in flight",
    roles: FINANCE_TIER,
    deepLink: "/finance?tab=payments",
  },
  flights: {
    key: "flights",
    icon: "🚁",
    title: "My Flights",
    roles: ["drone_pilot", "roof_specialist"],
    deepLink: "/drone",
  },
  rs_meetings: {
    key: "rs_meetings",
    icon: "🏠",
    title: "Adjuster Meetings",
    roles: ["roof_specialist", "roof_team_manager"],
    deepLink: "/dispatch",
  },
  preinsp: {
    key: "preinsp",
    icon: "🧗",
    title: "Pre-inspections",
    roles: ["roof_specialist", "roof_team_manager"],
    deepLink: "/dispatch",
  },

  // ---- Ship 3 ----------------------------------------------------------
  build_queue: {
    key: "build_queue",
    icon: "🔨",
    title: "Build Queue",
    // The two gates between "RM submitted" and "on Robert's calendar":
    // Chad approves, Robert schedules. Both see both — it is one handoff.
    roles: ["sales_director", "admin", "roof_team_manager"],
    deepLink: "/dispatch?view=chad&tab=approvals",
  },
  coaching: {
    key: "coaching",
    icon: "💬",
    title: "Coaching",
    // /ops is admin + sales_director only — keep the gate identical.
    roles: ["sales_director", "admin"],
    deepLink: "/ops?tab=coaching",
  },
  referrals: {
    key: "referrals",
    icon: "🤝",
    title: "Referrals",
    roles: ["sales_director", "admin"],
    deepLink: "/referrals",
  },
  builds_week: {
    key: "builds_week",
    icon: "🗓",
    title: "Builds this week",
    roles: ["roof_team_manager", "admin"],
    deepLink: "/dispatch?view=robert&tab=unscheduled",
  },
  roof_master: {
    key: "roof_master",
    icon: "📋",
    title: "Roof Master",
    roles: ["roof_team_manager", "admin"],
    deepLink: "/roof-master",
  },
  ops_requests: {
    key: "ops_requests",
    icon: "🛠",
    title: "Ops Requests",
    roles: ["roof_team_manager", "admin"],
    deepLink: "/requests",
  },
  my_areas: {
    key: "my_areas",
    icon: "📐",
    title: "My canvas areas",
    // Greg, Oct 19: "if a team member has been assigned to areas, have a
    // widget on their home page showing those". It self-hides for anyone with
    // no active area, so it can sit on every field board. Roles = who can be
    // put on an area (mirrors canvas_area_add_member). Canvassers and roof
    // specialists are eligible but have no default board — they land on
    // /canvas and /dispatch, where their area is already outlined.
    roles: [...SALES_ROLES, "canvasser", "roof_specialist"],
    deepLink: "/canvas",
  },
  pnl: {
    key: "pnl",
    icon: "💹",
    title: "Money pulse",
    // Money to exec eyes only. This is a GATE, not a board — see the
    // per-person override map below for who actually gets it.
    //
    // `director_of_operations` is listed for Jennifer, who is admitted to the
    // financials board as a PERSON, not as a role: `get_my_role()` returns
    // 'admin' for her via the super-user override, which is what clears
    // `financials_gate()` and `material_invoices` RLS (both live-verified by
    // JWT simulation Aug 26 — role 'admin', 1,428 invoice rows, real pulse).
    // A future dir-ops hire who is NOT a super user resolves to
    // 'director_of_operations', which the gate does NOT admit, so they would
    // get a 42501. Do NOT promote this to a `DEFAULT_WIDGETS_BY_ROLE` row —
    // the board is "Greg, Chad, Shannon, Jennifer, period" (Aug 21).
    roles: ["admin", "director_of_operations"],
    deepLink: "/financials",
    excludeEmails: ["jcohan@bardinroofing.com"],
  },
  company_revenue: {
    key: "company_revenue",
    icon: "🏗️",
    // Oct 27, 2026: the "Company build revenue" widget became the build revenue
    // tracker — built / scheduled / scoped per build month, Mine | Company.
    // Key kept so saved boards and the /team-boards editor carry over.
    title: "Build revenue",
    // Deliberately open to the field. Sums only, supplements excluded — it
    // leaks nothing sanitizeDealForField() protects, and the DB gate
    // (build_revenue_months) enforces the same list.
    roles: [...SALES_ROLES, "sales_director", "admin", "director_of_operations"],
    deepLink: "/build-calendar",
  },
  leaderboard: {
    key: "leaderboard",
    icon: "🏆",
    title: "Crew leaderboard",
    roles: ["admin", "sales_director", "roof_team_manager", "director_of_operations"],
    deepLink: "/crew",
  },
};

/**
 * Preloaded boards per role, in render (and mobile stack) order.
 * Ship 1 = the sales boards; Ship 2 added the ASM oversight widgets and the
 * finance board; Ship 3 adds Chad/Robert/exec.
 */
export const DEFAULT_WIDGETS_BY_ROLE: Record<string, WidgetKey[]> = {
  restore_manager: ["today", "my_areas", "pipeline", "call_list", "company_revenue"],
  team_lead: ["today", "my_areas", "pipeline", "call_list", "company_revenue"],
  // Greg: "for the asm, make it similar to rm. he still has his own pipeline
  // to cover" — same action layer FIRST, oversight widgets beside it.
  asm: ["today", "my_areas", "pipeline", "call_list", "approvals", "company_funnel", "company_revenue"],
  finance: ["ar", "queues", "iink"],
  // Ship 3 — Chad's ops board. The build gate he acts on, the coaching threads
  // where the ball is in his court, the referral program he approves, and the
  // company funnel he owns as sales director. Deliberately NO spiff widget:
  // the Spiff Week strip already renders the identical live race at the top of
  // every home page, and two copies of one number on one page is the bug class
  // the Ship 1 review killed ("triple going cold with 2 definitions").
  sales_director: ["build_queue", "coaching", "referrals", "company_funnel", "company_revenue"],
  // Ship 3 — Robert. Builds first (his calendar), then the queue feeding it,
  // then the climbs he assigns, then the grid he lives in.
  // Sep 25: the requests queue goes first — it's the list reps feed him.
  roof_team_manager: ["ops_requests", "builds_week", "build_queue", "preinsp", "roof_master"],
  // Ship 3 — Greg. One money row (the pulse RPC carries P&L + AR + SRS owed,
  // so there is no second money surface), the funnel, and the crew rank.
  admin: ["pnl", "company_revenue", "company_funnel", "leaderboard"],
};

/**
 * Hand-set per-person boards (team_roster.id → keys). Only used when someone's
 * measured usage diverges hard from their role. Never auto-populated.
 * Sam Houston (RS + drone secondary): 777 /my-work opens + 868 scheduled-tab
 * views in 60d — his board replaces the /my-work hub redirect.
 */
export const WIDGET_OVERRIDES_BY_MEMBER: Record<string, WidgetKey[]> = {
  "9b9ea8db-9897-43ec-a6ec-34d131027654": ["flights", "rs_meetings", "preinsp"],
  // Jennifer Ayers (director_of_operations) — Greg, Aug 26: "jennifer
  // definitely needs access to financial board". She already had it (super-user
  // bypass clears ProtectedRoute, filterDests, the page guard, financials_gate
  // and material_invoices RLS — all verified); this puts the money on her home
  // page instead of making her go find it. Hand-set per PERSON on purpose: the
  // financials board is a named-person board, so role-defaulting it would hand
  // a future non-super dir-ops hire a widget that 42501s. See `pnl` above.
  "0c8d09f1-f9b5-474b-90f6-aadf7f4066ed": ["pnl", "company_revenue", "company_funnel", "leaderboard"],
};

/** Canonical order for pickers (the /team-boards editor lists widgets in this order). */
export const WIDGET_ORDER: WidgetKey[] = [
  "today", "my_areas", "pipeline", "call_list", "company_revenue",
  "approvals", "company_funnel", "build_queue", "coaching", "referrals",
  "builds_week", "preinsp", "rs_meetings", "flights", "roof_master", "ops_requests",
  "ar", "queues", "iink", "pnl", "leaderboard",
];

/**
 * Can this person actually be shown this widget?
 *
 * Two gates, both mirrors of a server-side rule — never widen one without
 * widening its DB counterpart, or the widget renders an error instead of data:
 *   1. role gate      — the widget's board/RPC admits that role
 *   2. excludeEmails  — the per-person carve-out that BEATS super-user status
 *                       (Aug 21: Jeremy off /financials, enforced by
 *                       financials_gate() + material_invoices RLS)
 *
 * The /team-boards editor only offers keys that pass this, and the resolver
 * below re-applies it — so a saved board can never render a broken widget.
 */
export function isWidgetEligible(
  key: WidgetKey,
  role: string | null | undefined,
  email?: string | null,
): boolean {
  const def = WIDGETS[key];
  if (!def) return false;
  if (role && !def.roles.includes(role)) return false;
  const em = (email ?? "").trim().toLowerCase();
  if (em && def.excludeEmails?.some((x) => x.toLowerCase() === em)) return false;
  return true;
}

/** Why a widget is unavailable for this person — shown in the editor. */
export function ineligibleReason(
  key: WidgetKey,
  role: string | null | undefined,
  email?: string | null,
): string | null {
  const def = WIDGETS[key];
  if (!def) return "unknown widget";
  const em = (email ?? "").trim().toLowerCase();
  if (em && def.excludeEmails?.some((x) => x.toLowerCase() === em)) {
    return "excluded for this person by name";
  }
  if (role && !def.roles.includes(role)) {
    return `not available to ${role.replace(/_/g, " ")}`;
  }
  return null;
}

/** Where a person's board came from — drives the source chip in the editor. */
export type BoardSource = "custom" | "person" | "role" | "none";

export function boardSource(
  custom: WidgetKey[] | null | undefined,
  memberId: string | null | undefined,
  role: string | null | undefined,
): BoardSource {
  // A saved row wins even when it is empty — "no widgets" is a real choice.
  if (custom) return "custom";
  if (memberId && WIDGET_OVERRIDES_BY_MEMBER[memberId]) return "person";
  if (role && DEFAULT_WIDGETS_BY_ROLE[role]) return "role";
  return "none";
}

/** The board this person gets when nobody has customized it. */
export function defaultWidgetsFor(
  memberId: string | null | undefined,
  role: string | null | undefined,
  email?: string | null,
): WidgetKey[] {
  const keys =
    (memberId && WIDGET_OVERRIDES_BY_MEMBER[memberId]) ||
    (role && DEFAULT_WIDGETS_BY_ROLE[role]) ||
    [];
  return keys.filter((k) => isWidgetEligible(k, role, email));
}

/**
 * The widget keys this user's home board should render, in order.
 *
 * Precedence: a saved `user_widget_layout` row (set from /team-boards) BEATS
 * the hand-set per-person override in this file, which beats the role default.
 * Clearing the saved row falls straight back to the code defaults.
 *
 * `custom` is the raw jsonb array from that row (null = nothing saved). It is
 * filtered through `isWidgetEligible` exactly like the defaults are, so a row
 * written before someone changed roles can never render a broken widget.
 */
export function resolveWidgets(
  custom: WidgetKey[] | null | undefined,
  memberId: string | null | undefined,
  role: string | null | undefined,
  email?: string | null,
): WidgetKey[] {
  if (custom) {
    const filtered = custom.filter((k) => isWidgetEligible(k, role, email));
    // An empty saved row is a deliberate "no board" and is honored. A row that
    // had widgets but whose keys ALL became ineligible is a stale row (someone
    // changed roles) — fall through to the default rather than blanking their
    // home page. `null` (no row at all) always falls through.
    if (custom.length === 0 || filtered.length > 0) return filtered;
  }
  return defaultWidgetsFor(memberId, role, email);
}

/**
 * Code-default board for this user (no saved row considered).
 * `email` is optional so older call sites keep compiling, but pass it wherever
 * you have it — it is what enforces `excludeEmails` (the financials carve-out).
 */
export function widgetsForUser(
  memberId: string | null | undefined,
  role: string | null | undefined,
  email?: string | null,
): WidgetKey[] {
  return defaultWidgetsFor(memberId, role, email);
}
